import { sessionManager } from '../../../utils/sessionManager';

export const isSameUser = (id1, id2) => {
  if (!id1 || !id2) return false;
  const str1 = String(id1).trim();
  const str2 = String(id2).trim();
  if (str1 === str2) return true;

  const num1 = parseInt(str1, 10);
  const num2 = parseInt(str2, 10);
  if (Number.isNaN(num1) || Number.isNaN(num2)) return false;
  return num1 === num2;
};

export const extractInfinityCode = (data) => {
  if (!data || typeof data !== 'object') return null;

  const possibleFields = [
    'infinityCode', 'InfinityCode', 'Infinity Code', 'INFINITY_CODE',
    'infinity_code', 'infinity-code', 'infinitycode', 'INFINITYCODE',
    'id', 'ID', 'Id', '_id',
    'userId', 'user_id', 'USER_ID', 'UserId', 'userid', 'USERID',
    'employeeId', 'employee_id', 'EMPLOYEE_ID', 'EmployeeId', 'employeeid',
    'code', 'Code', 'CODE',
    'empCode', 'emp_code', 'EmpCode', 'empcode',
    'userCode', 'user_code', 'UserCode', 'usercode',
  ];

  for (const field of possibleFields) {
    if (data[field] !== undefined && data[field] !== null) {
      const value = String(data[field]).trim();
      const numValue = parseInt(value, 10);
      if (!Number.isNaN(numValue) && numValue > 0) {
        return numValue;
      }
    }
  }

  const nestedKeys = ['user', 'employee', 'data', 'profile', 'info'];
  for (const nestedKey of nestedKeys) {
    if (data[nestedKey] && typeof data[nestedKey] === 'object') {
      const nested = extractInfinityCode(data[nestedKey]);
      if (nested) return nested;
    }
  }

  return null;
};

export const extractUserName = (data, language = 'en') => {
  if (!data) return 'You';

  if (language === 'ar' && data.arabicName && data.arabicName.trim()) {
    return data.arabicName.trim();
  }

  const nameFields = [
    'sr', 'SR', 'SR.', 'Sr', 'sr.',
    'name', 'Name', 'NAME',
    'fullName', 'full_name', 'FullName', 'FULL_NAME',
    'username', 'userName', 'UserName', 'USERNAME',
  ];

  for (const field of nameFields) {
    if (data[field] && typeof data[field] === 'string' && data[field].trim()) {
      return data[field].trim();
    }
  }

  if (data.user) return extractUserName(data.user, language);
  if (data.employee) return extractUserName(data.employee, language);

  return 'You';
};

export const extractUserEmail = (data) => {
  if (!data) return '';

  const emailFields = ['email', 'Email', 'EMAIL', 'mail', 'userEmail'];

  for (const field of emailFields) {
    if (data[field] && typeof data[field] === 'string' && data[field].includes('@')) {
      return data[field].trim();
    }
  }

  return '';
};

export const getUserInfoFromLocalStorage = (profile = null) => {
  // Prefer profile from /users/me (via useProfile hook) over localStorage
  const infinityCode = profile?.infinityCode || localStorage.getItem('infinityCode');
  // Display name from the server-confirmed profile only.
  const employeeName = profile?.employeeName || profile?.employeeArabicName || null;
  const email = profile?.employeeEmail || localStorage.getItem('email') || localStorage.getItem('employeeEmail');

  if (infinityCode) {
    return {
      infinityCode: String(infinityCode),
      name: employeeName || 'Unknown',
      sr: employeeName || 'Unknown',
      email: email || '',
    };
  }

  const possibleStorageKeys = [
    'userInfo', 'user', 'currentUser', 'authUser',
    'employeeInfo', 'employee', 'currentEmployee',
  ];

  for (const key of possibleStorageKeys) {
    const data = localStorage.getItem(key);
    if (!data) continue;

    try {
      const parsed = JSON.parse(data);
      const infinityCodeFromObj = extractInfinityCode(parsed);
      if (infinityCodeFromObj) {
        return parsed;
      }
    } catch (e) {
      continue;
    }
  }

  return null;
};

export const getDisplayName = (user, language) => {
  if (!user) return 'User';

  return language === 'ar'
    ? user.arabicName || user.sr || user.name
    : user.sr || user.name;
};

// Each role's dashboard shell owns its OWN "messages" nav item and syncs its
// internal page state from the last URL segment (see Admin_Acount.jsx,
// Manager_Account's useSyncPageWithRoute, User_Account/index.jsx — all three
// match `location.pathname`'s last part against a nav item keyed "messages").
// Building the path from the user's role — instead of guessing from
// whatever page the mini widget happens to be opened on — means "open full
// inbox" always lands on a real messages page, regardless of which page in
// the app it was clicked from.
const ROLE_SHELL_PATH = {
  admin: '/dashboard',
  superadmin: '/DashboardSuper',
  manager: '/DashboardManager',
  user: '/userprofile',
};

// Reads the role the same way `PrivateRoute` in App.js does (session
// manager first, falling back to the raw localStorage key) — the shared
// `currentUser` object the messenger module builds doesn't carry a role
// field at all, so that isn't a usable source here.
export const getFullInboxPath = () => {
  const role = sessionManager.getUserRole() || localStorage.getItem('role');
  const base = ROLE_SHELL_PATH[String(role || '').toLowerCase()] || '/userprofile';
  return `${base}/messages`;
};